import axios from 'axios'
import dayjs from 'dayjs'

import type {
  DateTimeMs,
  EditorGrabEvent,
  EditorImage,
  EventId,
  GroupId,
  ProgramId,
  SceneId,
} from '@/shared/types/applicationData.type'
import { type MusicGroup } from '@/shared/types/musicGroup/musicGroup.type'
import {
  type EventAttendeesDTO,
  type ProgEvent,
} from '@/shared/types/progEvent.type'

import { type PageContent } from '../menuEntries/pageSetup/pageContent.type'
import { type ImportResult } from './progEvent.store'

// === Editor → Preview adapter ===
//
// Converts the backoffice's `ProgEvent` editor model into the editor-widened
// `EditorGrabEvent` slice that `PreviewGrabData.events` accepts. The branded
// id types (`EventId` / `GroupId` / `SceneId` / `ProgramId`) are cast at
// assignment — the editor only knows raw strings — but the field shape and
// required-ness are checked against the strict mobile contract.
export type AdaptProgEventInput = {
  // `Partial<ProgEvent>` — the Adding page calls this while the user is
  // still filling in fields. Missing values fall back to safe defaults
  // (`'create'` id, empty title, false flags, …) below.
  event: Partial<ProgEvent> | null | undefined
  croppedImage: string | null
  fileImage: string | undefined | null
  firstGroupWithImage: MusicGroup | undefined
  mainGuest: Partial<MusicGroup> | undefined
  groupIds: string[]
  genreIds: string[]
}

export function getMainGuest(
  groups: MusicGroup[] | undefined,
): MusicGroup | undefined {
  if (!groups || groups.length === 0) return undefined
  return [...groups].sort((a, b) => {
    const weightA = a.weight ?? 0
    const weightB = b.weight ?? 0
    if (weightB !== weightA) {
      return weightB - weightA
    }
    const nameA = a.names.fr
    const nameB = b.names.fr
    return nameA.localeCompare(nameB)
  })[0]
}

function resolveEventTitle(
  event?: Partial<DBProgEvent> | null,
  mainGuest?: Partial<MusicGroup>,
): string {
  const eventTitle = event?.titles?.fr
  if (eventTitle != null && eventTitle !== '') {
    return eventTitle
  }
  const groupNames = (event?.musicGroups ?? [])
    .map((g) => g.names.fr)
    .filter((n) => n !== '')
    .join(' & ')
  if (groupNames !== '') {
    return groupNames
  }
  return mainGuest?.names?.fr ?? ''
}

function resolveEventDescription(
  event?: Partial<DBProgEvent> | null,
  mainGuest?: Partial<MusicGroup>,
): string {
  const eventDesc = event?.descriptions?.fr
  if (eventDesc != null && eventDesc !== '') {
    return eventDesc
  }
  return mainGuest?.descriptions?.fr ?? ''
}

export function adaptProgEvent({
  event,
  croppedImage,
  fileImage,
  firstGroupWithImage,
  mainGuest,
  groupIds,
  genreIds,
}: AdaptProgEventInput): EditorGrabEvent {
  const day = new Date(event?.date ?? 0).getTime() as DateTimeMs
  const showStartDate = new Date(
    `${event?.date} ${event?.startTime}`,
  ).getTime() as DateTimeMs
  const showEndDate = new Date(
    `${event?.date} ${event?.endTime}`,
  ).getTime() as DateTimeMs
  const isDataUrl = (v: unknown): v is string =>
    typeof v === 'string' && (v.startsWith('data:') || v.startsWith('zdata:'))
  const sanitizeImageField = (
    v: EditorImage | string | undefined | null,
  ): EditorImage | undefined => {
    if (v == null) return undefined
    if (typeof v === 'string') return isDataUrl(v) ? undefined : v
    if (
      typeof v === 'object' &&
      'previewDataUri' in v &&
      isDataUrl((v as { previewDataUri: string }).previewDataUri)
    ) {
      return undefined
    }
    return v as EditorImage
  }
  const fallbackImage: EditorImage | undefined =
    croppedImage != null && croppedImage !== ''
      ? { previewDataUri: croppedImage }
      : sanitizeImageField(fileImage ?? firstGroupWithImage?.image ?? undefined)
  return {
    id: (event?._id ?? 'create') as EventId,
    // without it the edited event goes back to being a root event in the preview:
    // it leaves `subEvents`, and its guests vanish from the parent's line-up
    parentEventId: (event?.parentEventId ?? undefined) as EventId | undefined,
    musicGroupsIds: groupIds as GroupId[],
    sceneId: (event?.scene?._id ?? '') as SceneId,
    day,
    showStartDate,
    showEndDate,
    title: resolveEventTitle(event, mainGuest),
    image: fallbackImage,
    genres: genreIds as EditorGrabEvent['genres'],
    programId: (event?.program?._id ?? '') as ProgramId,
    eventTicketLink: event?.eventTicketLink ?? '',
    copyright: event?.copyright ?? firstGroupWithImage?.copyright ?? '',
    description: resolveEventDescription(event, mainGuest),
    lineUp: event?.lineUp?.fr ?? mainGuest?.lineUp?.fr ?? undefined,
    enableAttending: event?.enableAttending ?? false,
    enableRating: event?.enableRating ?? false,
    weight: undefined,
    ticketOpenInWebview: event?.ticketOpenInWebview ?? false,
    hideEndDate: event?.hideEndDate ?? false,
    hideLocation: event?.hideLocation ?? false,
    price: event?.prices?.fr ?? '',
    soldOut: event?.soldOut ?? false,
  }
}

export type DBProgEvent = Omit<
  ProgEvent,
  | 'fakeTitle'
  | 'startTime'
  | 'date'
  | 'endTime'
  | 'attendees'
  | 'eventAttendeesList'
> & {
  eventAttendeesList?: EventAttendeesDTO[] | null
}

export function eventsToDbFormat(events?: ProgEvent[]): DBProgEvent[] | null {
  if (events == null) return null
  return events.map((e) => eventToDbFormat(e))
}

function eventToDbFormat(event: ProgEvent): DBProgEvent {
  event.day = dayjs(event.date).toISOString()
  event.showStartDate = dayjs(event.day)
    .add(getDayOffset(event.startTime), 'days')
    .hour(parseInt(event.startTime.split(':')[0]))
    .minute(parseInt(event.startTime.split(':')[1]))
    .toISOString()
  event.showEndDate = dayjs(event.day)
    .add(getDayOffset(event.endTime), 'days')
    .hour(parseInt(event.endTime.split(':')[0]))
    .minute(parseInt(event.endTime.split(':')[1]))
    .toISOString()
  if (dayjs(event.showEndDate).isBefore(dayjs(event.showStartDate))) {
    event.showEndDate = dayjs(event.showEndDate).add(1, 'days').toISOString()
  }

  const {
    date: _date,
    startTime: _startTime,
    endTime: _endTime,
    fakeTitle: _fakeTitle,
    attendees: _attendees,
    ...rest
  } = event
  return rest
}

// offset by one day if below 06:00
export function getDayOffset(time: dayjs.ConfigType): 1 | 0 {
  return dayjs(time, 'HH:mm').format('HH:mm') < '06:00' ? 1 : 0
}

export function eventToJsFormat(event: DBProgEvent): ProgEvent {
  return {
    ...event,
    descriptions: event.descriptions ?? { fr: '', en: '' },
    lineUp: event.lineUp ?? { fr: '', en: '' },
    fakeTitle: computeFakeTitle(event),
    date: dayjs(event.day).format('YYYY-MM-DD'),
    startTime: dayjs(event.showStartDate).format(
      'HH:mm',
    ) as `${number}:${number}`,
    endTime: dayjs(event.showEndDate).format('HH:mm') as `${number}:${number}`,
    eventAttendeesList: event.eventAttendeesList ?? [],
    attendees: event.eventAttendeesList?.length ?? 0,
  }
}

export function computeFakeTitle(event: DBProgEvent): string {
  const title = event.titles?.fr
  if (title != null && title !== '') {
    return title
  }
  return (event.musicGroups ?? [])
    .map((guest) => guest.names.fr)
    .filter((name) => name !== '')
    .join(' & ')
}

/** Validates that an item has a non-empty string identifier. */
export function hasValidId(item: unknown): boolean {
  if (item == null || typeof item !== 'object') {
    return false
  }
  const id = (item as { _id?: unknown })._id
  return typeof id === 'string' && id.trim().length > 0
}

export type ProgramEventUsage = {
  pageContents: PageContent[]
}

export const ProgEventService = {
  async getAll(): Promise<ProgEvent[]> {
    const { data } = await axios.get<DBProgEvent[]>('/api/programEvent')
    return data.map((data: DBProgEvent) => eventToJsFormat(data))
  },

  async getProgramEventByProgram(id: string): Promise<ProgEvent[]> {
    const { data } = await axios.get<DBProgEvent[]>(
      `/api/programEvent/byProgram/${id}`,
    )
    return data.map((data: DBProgEvent) => eventToJsFormat(data))
  },

  async getProgramEventById(id: string): Promise<ProgEvent> {
    const { data } = await axios.get<DBProgEvent>(`/api/programEvent/${id}`)
    return eventToJsFormat(data)
  },

  async deleteProgramEvent(id: string): Promise<void> {
    await axios.delete(`/api/programEvent/${id}`)
  },

  async addProgramEvent(programEvent: ProgEvent): Promise<ProgEvent> {
    const { data } = await axios.post<DBProgEvent>(
      '/api/programEvent',
      eventToDbFormat(programEvent),
    )
    return eventToJsFormat(data)
  },

  async updateProgramEvent(programEvent: ProgEvent): Promise<ProgEvent> {
    const { data } = await axios.put<DBProgEvent>(
      `/api/programEvent`,
      eventToDbFormat(programEvent),
    )
    return eventToJsFormat(data)
  },
  async hideShowBatch(ids: string[], show: boolean): Promise<void> {
    await axios.post('/api/programEvent/batch-hide-show', { ids, show })
  },

  async checkUsage(ids: string[]): Promise<ProgramEventUsage> {
    const { data } = await axios.get<ProgramEventUsage>(
      '/api/programEvent/check-usage',
      {
        params: { ids: ids.join(',') },
      },
    )
    return data
  },

  async editShowBatch(
    ids: string[],
    programId?: string,
    sceneId?: string,
  ): Promise<void> {
    await axios.post('/api/programEvent/batch-edit-show', {
      ids,
      programId,
      sceneId,
    })
  },
  async multipleDelete(ids: string[]): Promise<void> {
    await axios.post(`/api/programEvent/batch-delete`, ids)
  },
  async uploadFile(
    file: string | ArrayBuffer | null,
    visible = false,
  ): Promise<string> {
    const { data } = await axios.post<string>(
      '/api/programEvent/batch/importXls',
      {
        file,
      },
      { params: { visible } },
    )
    return data
  },
  async getImportResult(batchId: string): Promise<ImportResult> {
    const { data } = await axios.get<ImportResult>(
      `/api/programEvent/batch/importResult/${batchId}`,
    )
    return data
  },
}
